import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { useSettings } from '@/hooks/state/useSettings';

import {
  POST_DOWNLOAD_DATA_CAPTURE_DELAY_MS,
  postDownloadDataCaptureAtom,
} from './postDownloadDataCapture';
import { useStage } from './useStage';

/**
 * Presents the data-capture questionnaire to users who created their account
 * via a download attempt, once their first download has completed (DEV-6521).
 *
 * When the machine is `pending` (set on that first completed download) and the
 * stage is idle, it waits {@link POST_DOWNLOAD_DATA_CAPTURE_DELAY_MS} and then
 * opens `onboarding.auth-gate` — the standard entry into the questionnaire
 * flow. Deferring rather than showing it inline keeps the download the user
 * came for from being interrupted.
 *
 * Because `pending` is persisted in localStorage, a user who closes the site
 * before the delay elapses is picked up here on their next session (the
 * ticket's secondary trigger): the same `pending` → wait → open path runs on
 * mount.
 *
 * Gated by `new-download-flow` — the flag the whole journey lives behind — so
 * it's inert for everyone outside the flagged experience.
 */
export const usePostDownloadDataCapture = () => {
  const isDownloadFlowOn = useFeatureIsOn('new-download-flow');
  const [state, setState] = useAtom(postDownloadDataCaptureAtom);
  const { open, current } = useStage();
  const { settings } = useSettings();
  const isAuthenticated = settings?.user?.is_authenticated === true;

  useEffect(() => {
    if (!(isDownloadFlowOn && isAuthenticated) || state !== 'pending') {
      return;
    }
    // Don't pop the prompt over an open flow — most importantly the download
    // journey itself, which is still on screen the moment the download
    // completes. The effect re-runs when the stage closes and arms the wait
    // then, so the timer only counts down while the user is browsing.
    if (current !== null) {
      return;
    }

    const timer = window.setTimeout(() => {
      // Reset before opening so the prompt is a one-shot: the state change
      // re-runs the effect, which now short-circuits on `idle`.
      setState('idle');
      open('onboarding.auth-gate');
    }, POST_DOWNLOAD_DATA_CAPTURE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isDownloadFlowOn, isAuthenticated, state, current, open, setState]);
};
